import type { ReactNode } from 'react'

import { Switch, Text, Tooltip } from '@mantine/core'
import { TimeInput } from '@mantine/dates'

import IconAdd from 'src/icons/IconAdd'
import IconClose from 'src/icons/IconClose'
import IconCopy from 'src/icons/IconCopy'
import IconInfoCircle from 'src/icons/IconInfoCircle'

// Weekday values mirror the `Weekday` GraphQL enum. The generated web type is a
// string-literal union (type-only), so we keep a runtime array here to iterate
// and render days; `Weekday` below is structurally identical to the generated type.
export const WEEKDAYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const

export type Weekday = (typeof WEEKDAYS)[number]

const WEEKDAY_LABELS: Record<Weekday, string> = {
  SUNDAY: 'Sunday',
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
}

/**
 * A single active-hours row as it lives on the rule. `startMinute`/`endMinute`
 * are minutes-from-midnight (0–1440). `endMinute <= startMinute` denotes an
 * overnight window that wraps past midnight (flagged "next day" in the UI).
 */
export interface ActiveHour {
  activeHoursId?: string | null
  dayOfWeek: Weekday
  startMinute: number
  endMinute: number
  __typename?: string
}

interface Range {
  activeHoursId?: string | null
  start: number
  end: number
}

type DayGroups = Record<Weekday, Range[]>

interface RuleActiveHoursProps {
  /** Current active-hours rows (controlled). Empty means "runs at all hours". */
  activeHours?: ActiveHour[]
  /** Called with the next full set of rows on every edit. */
  onChange: (next: ActiveHour[]) => void
  /** Company timezone, shown read-only. Falls back to a neutral label. */
  timezone?: string | null
}

// 9:00 AM – 5:00 PM business hours. The default active windows are everything
// OUTSIDE this range, so a freshly enabled day covers the early morning and
// evening — the common "reach drivers off the clock" pattern.
const DEFAULT_START = 540
const DEFAULT_END = 1020
const MINUTES_IN_DAY = 1440

// Windows a freshly enabled day picks up. Weekdays cover everything outside
// 9-5 (12:00 AM–9:00 AM and 5:00 PM–midnight); weekends run all day.
const defaultDayRanges = (day: Weekday): Range[] =>
  day === 'SATURDAY' || day === 'SUNDAY'
    ? [{ start: 0, end: MINUTES_IN_DAY }]
    : [
        { start: 0, end: DEFAULT_START },
        { start: DEFAULT_END, end: MINUTES_IN_DAY },
      ]

const pad = (n: number): string => (n < 10 ? '0' : '') + n

/** Minutes-from-midnight → "HH:mm" (24h, for the TimeInput value). */
export const minutesToHHMM = (minutes: number): string => {
  const m = ((minutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`
}

/** "HH:mm" → minutes-from-midnight. */
export const hhmmToMinutes = (value: string): number => {
  const [h, m] = value.split(':')
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0)
}

/** Minutes-from-midnight → 12h label (e.g. "9:00 AM"). */
export const formatMinutesLabel = (minutes: number): string => {
  const m = ((minutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY
  const h = Math.floor(m / 60)
  const mm = m % 60
  const ampm = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${pad(mm)} ${ampm}`
}

/** True when a range wraps past midnight (end at or before start). */
export const isOvernight = (range: Range): boolean => range.end <= range.start

/**
 * Group the flat active-hours rows into per-weekday range lists, preserving
 * `activeHoursId` so edits update existing rows in place. A weekday is
 * considered "enabled" when it has at least one range.
 */
export const groupActiveHoursByDay = (
  activeHours: ActiveHour[] | undefined | null
): DayGroups => {
  const groups = WEEKDAYS.reduce((acc, day) => {
    acc[day] = []
    return acc
  }, {} as DayGroups)

  ;(activeHours ?? []).forEach((row) => {
    if (!groups[row.dayOfWeek]) return
    groups[row.dayOfWeek].push({
      activeHoursId: row.activeHoursId ?? undefined,
      start: row.startMinute,
      end: row.endMinute,
    })
  })

  return groups
}

const flattenGroups = (groups: DayGroups): ActiveHour[] => {
  const next: ActiveHour[] = []
  WEEKDAYS.forEach((day) => {
    groups[day].forEach((range) => {
      next.push({
        ...(range.activeHoursId ? { activeHoursId: range.activeHoursId } : {}),
        dayOfWeek: day,
        startMinute: range.start,
        endMinute: range.end,
      })
    })
  })
  return next
}

const cloneGroups = (groups: DayGroups): DayGroups =>
  WEEKDAYS.reduce((acc, day) => {
    acc[day] = groups[day].map((range) => ({ ...range }))
    return acc
  }, {} as DayGroups)

const summarizeRanges = (ranges: Range[]): string =>
  ranges
    .map(
      (range) =>
        `${formatMinutesLabel(range.start)} – ${formatMinutesLabel(range.end)}${
          isOvernight(range) ? ' (next day)' : ''
        }`
    )
    .join(', ')

/**
 * One-line summary of a rule's active hours, for the collapsed "Runs …" tag.
 * No windows → "Runs all hours". Uniform windows across the enabled days →
 * "Runs <days> · <times>". Otherwise → "Runs custom hours".
 */
export const summarizeActiveHours = (
  activeHours: ActiveHour[] | undefined | null
): string => {
  const groups = groupActiveHoursByDay(activeHours)
  const enabledDays = WEEKDAYS.filter((day) => groups[day].length > 0)
  if (enabledDays.length === 0) return 'Runs all hours'

  const uniform = enabledDays.every(
    (day) =>
      summarizeRanges(groups[day]) === summarizeRanges(groups[enabledDays[0]])
  )
  if (!uniform) return 'Runs custom hours'

  const days =
    enabledDays.length === 7
      ? 'every day'
      : enabledDays.map((day) => WEEKDAY_LABELS[day].slice(0, 3)).join(', ')
  return `Runs ${days} · ${summarizeRanges(groups[enabledDays[0]])}`
}

const IconButton = ({
  label,
  onClick,
  children,
  danger = false,
}: {
  label: string
  onClick: () => void
  children: ReactNode
  danger?: boolean
}) => (
  <Tooltip label={label} position="top" withArrow arrowPosition="center">
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-transparent text-doubleNickel-gray-400 transition-colors hover:border-doubleNickel-gray-200 hover:bg-doubleNickel-gray-50 ${
        danger
          ? 'hover:text-doubleNickel-error-500'
          : 'hover:text-doubleNickel-gray-700'
      }`}
    >
      {children}
    </button>
  </Tooltip>
)

const RuleActiveHours = ({
  activeHours = [],
  onChange,
  timezone,
}: RuleActiveHoursProps) => {
  const groups = groupActiveHoursByDay(activeHours)

  const commit = (nextGroups: DayGroups) => {
    onChange(flattenGroups(nextGroups))
  }

  const toggleDay = (day: Weekday, enabled: boolean) => {
    const next = cloneGroups(groups)
    next[day] = enabled ? defaultDayRanges(day) : []
    commit(next)
  }

  const addRange = (day: Weekday) => {
    const next = cloneGroups(groups)
    const last = next[day][next[day].length - 1]
    // Start the new window where the previous one ended (bounded so it never
    // opens at midnight-of-next-day) — mirrors the design playground.
    const start = last ? Math.min(last.end, MINUTES_IN_DAY - 60) : DEFAULT_START
    next[day].push({
      start,
      end: Math.min(start + 60, MINUTES_IN_DAY),
    })
    commit(next)
  }

  const removeRange = (day: Weekday, index: number) => {
    const next = cloneGroups(groups)
    next[day].splice(index, 1)
    commit(next)
  }

  const updateRange = (
    day: Weekday,
    index: number,
    field: 'start' | 'end',
    value: string
  ) => {
    if (!value) return
    let minutes = hhmmToMinutes(value)
    // The native time picker can't express 24:00, so a midnight END ("00:00")
    // means "end of day" (1440), not 0 — otherwise a same-day "until midnight"
    // window round-trips to 0 and the backend rejects it (endMinute >= 1). A
    // midnight START stays 0 (start of day).
    if (field === 'end' && minutes === 0) minutes = MINUTES_IN_DAY
    const current = groups[day][index]
    const nextStart = field === 'start' ? minutes : current.start
    const nextEnd = field === 'end' ? minutes : current.end
    // Overnight windows aren't allowed in the UI: reject any edit that would
    // put the end time at or before the start (which would wrap past midnight).
    if (nextEnd <= nextStart) return
    const next = cloneGroups(groups)
    next[day][index] = { ...next[day][index], [field]: minutes }
    commit(next)
  }

  const copyToAllDays = (day: Weekday) => {
    const next = cloneGroups(groups)
    WEEKDAYS.forEach((other) => {
      if (other === day) return
      // Copied rows start fresh (no activeHoursId) so the backend treats them
      // as new rows for those days.
      next[other] = next[day].map((range) => ({
        start: range.start,
        end: range.end,
      }))
    })
    commit(next)
  }

  const restricted = WEEKDAYS.some((day) => groups[day].length > 0)

  const toggleRestricted = (on: boolean) => {
    if (!on) {
      // Clearing all windows returns the rule to "runs at all hours".
      commit(groupActiveHoursByDay([]))
      return
    }
    // Seed every day with the outside-business-hours default so the freshly
    // revealed editor starts sensible; the user narrows days/times from there.
    const next = groupActiveHoursByDay([])
    WEEKDAYS.forEach((day) => {
      next[day] = defaultDayRanges(day)
    })
    commit(next)
  }

  return (
    <div className="space-y-4">
      {/* Restrict toggle + timezone helper */}
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-1">
          <Switch
            size="xs"
            checked={restricted}
            onChange={(e) => toggleRestricted(e.currentTarget.checked)}
            label="Restrict hours"
            aria-label="Restrict hours"
            classNames={{ label: 'text-xs text-doubleNickel-gray-500' }}
          />
          <Tooltip
            label="By default this rule runs at all hours. Turn on to limit it to specific days and hours — outside those windows the rule is skipped, and recurring rules try again on their next scheduled run."
            position="bottom"
            withArrow
            arrowPosition="center"
            multiline
            w={240}
          >
            <div>
              <IconInfoCircle className="h-3 w-3 fill-none stroke-doubleNickel-brand-500" />
            </div>
          </Tooltip>
        </div>
        {/* Company timezone is read-only helper text (no picker). Sourced from
            the current company (GET_MY_COMPANIES); falls back to a neutral
            label if a company hasn't set one. */}
        {restricted && (
          <Text size="xs" className="text-doubleNickel-gray-400">
            Times in {timezone || 'company timezone'}
          </Text>
        )}
      </div>

      {!restricted ? (
        <Text size="xs" className="text-doubleNickel-gray-500">
          This rule runs at <b>all hours</b>. Turn on to limit it to specific
          days and times.
        </Text>
      ) : (
        // Weekday list
        <div className="flex flex-col">
          {WEEKDAYS.map((day) => {
            const ranges = groups[day]
            const enabled = ranges.length > 0
            return (
              <div
                key={day}
                className="grid grid-cols-[132px_1fr_auto] items-start gap-3 border-t border-doubleNickel-gray-100 first:border-t-0"
              >
                {/* Toggle + day name */}
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    size="xs"
                    checked={enabled}
                    onChange={(e) => toggleDay(day, e.currentTarget.checked)}
                    aria-label={`${WEEKDAY_LABELS[day]} enabled`}
                  />
                  <Text
                    size="xs"
                    fw={600}
                    className={
                      enabled
                        ? 'text-doubleNickel-gray-900'
                        : 'text-doubleNickel-gray-400'
                    }
                  >
                    {WEEKDAY_LABELS[day]}
                  </Text>
                </div>

                {/* Ranges */}
                <div className="flex min-h-[32px] flex-col gap-2">
                  {!enabled ? (
                    <Text
                      size="xs"
                      className="pt-1.5 text-doubleNickel-gray-400"
                    >
                      Unavailable
                    </Text>
                  ) : (
                    ranges.map((range, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <TimeInput
                          size="xs"
                          aria-label={`${WEEKDAY_LABELS[day]} start time`}
                          classNames={{ input: 'text-xs w-[92px]' }}
                          value={minutesToHHMM(range.start)}
                          onChange={(e) =>
                            updateRange(
                              day,
                              index,
                              'start',
                              e.currentTarget.value
                            )
                          }
                        />
                        <span className="text-doubleNickel-gray-400">–</span>
                        <TimeInput
                          size="xs"
                          aria-label={`${WEEKDAY_LABELS[day]} end time`}
                          classNames={{ input: 'text-xs w-[92px]' }}
                          value={minutesToHHMM(range.end)}
                          onChange={(e) =>
                            updateRange(
                              day,
                              index,
                              'end',
                              e.currentTarget.value
                            )
                          }
                        />
                        <IconButton
                          label="Remove range"
                          onClick={() => removeRange(day, index)}
                          danger
                        >
                          <IconClose className="h-2 w-2 fill-none stroke-current" />
                        </IconButton>
                      </div>
                    ))
                  )}
                </div>

                {/* Per-day actions */}
                <div className="flex items-center gap-1 pt-1">
                  <IconButton
                    label="Add another range"
                    onClick={() => addRange(day)}
                  >
                    <IconAdd className="h-3.5 w-3.5 fill-none stroke-current" />
                  </IconButton>
                  {enabled && (
                    <IconButton
                      label="Copy this day to all days"
                      onClick={() => copyToAllDays(day)}
                    >
                      <IconCopy className="h-3.5 w-3.5 fill-none stroke-current" />
                    </IconButton>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default RuleActiveHours
